import { useQueryClient } from '@tanstack/react-query';
import * as R from 'ramda';
import useInfinityListQuery from '../../../components/Hooks/useInfinityListQuery';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import {
  PhaseType,
  SalesOpportunity,
  SalesOpportunityConversion,
  SalesOpportunityConversionByCategory,
} from '../../../modules/sales-opportunity/salesOpportunity.types';
import {
  getSalesOpportunityCategoriesQueryKey,
  getSalesOpportunityConversionQueryKey,
  getSalesOpportunityQueryKey,
} from './orderbook.keys';
import {
  getSalesOpportunities,
  getSalesOpportunitiesCategories,
  getSalesOpportunityConversion,
} from './orderbook.repository';
import {
  UseCategoryConversionsProps,
  UseGetSalesOpportunityProps,
  UseSalesOpportunityCategoriesProps,
  UseSalesOpportunityConversionProps,
} from './orderbook.types';

const STALE_TIME = 3 * 60 * 1000;

export function useGetSalesOpportunities({ customerId }: UseGetSalesOpportunityProps) {
  return useInfinityListQuery<SalesOpportunity>({
    queryKey: getSalesOpportunityQueryKey(customerId),
    fetcher: getSalesOpportunities(),
    listOptions: {
      itemsPerPage: 4,
      phase: [PhaseType.OPEN, PhaseType.SCHEDULED],
      customerIds: [customerId],
      minimumOpportunityValue: 1,
    },
    useUrlParams: false,
  });
}

export function useSalesOpportunityCategories({
  salesOpportunityId,
  shouldFetch,
}: UseSalesOpportunityCategoriesProps) {
  return useInfinityListQuery<SalesOpportunityConversionByCategory>({
    queryKey: getSalesOpportunityCategoriesQueryKey(salesOpportunityId),
    fetcher: getSalesOpportunitiesCategories(salesOpportunityId),
    listOptions: {
      itemsPerPage: 10,
    },
    config: {
      shouldFetch,
    },
    useUrlParams: false,
  });
}

export function useConversions<T>({
  queryKey,
  enabled,
  idsToFetchConversion,
  groupBy,
  apiCallFn,
}: UseCategoryConversionsProps<T>) {
  const queryClient = useQueryClient();

  const fetchConversions = async (storedConversions?: Record<string, T>) => {
    const conversionIdsAlreadyFetched = storedConversions ? Object.keys(storedConversions) : [];

    const idsToGetConversion = R.difference(idsToFetchConversion, conversionIdsAlreadyFetched);

    if (idsToGetConversion.length) {
      const response = await apiCallFn(idsToGetConversion);

      if (response.ok && response.data) {
        const arrayToObjectWithUniqueKeys = R.reduce(
          (acc: Record<string, T>, obj: T) => R.assoc(obj[groupBy] as string, obj, acc),
          {} as Record<string, T>,
        );

        const uniqueKeysObject = arrayToObjectWithUniqueKeys(response.data);

        return { ...storedConversions, ...uniqueKeysObject };
      }

      return storedConversions ?? {};
    }

    return storedConversions ?? {};
  };

  return useDataQuery<Record<string, T>>(
    {
      queryKey,
      queryFn: async () => {
        const storedConversions = queryClient.getQueryData<Record<string, T>>(queryKey);
        return fetchConversions(storedConversions);
      },
      staleTime: STALE_TIME,
    },
    {
      enabled,
    },
  );
}

export const useSalesOpportunityConversion = ({
  enabled,
  idsToFetchConversion,
  customerId,
}: UseSalesOpportunityConversionProps) =>
  useConversions<SalesOpportunityConversion>({
    apiCallFn: (idsToGetConversion) => {
      const conversionParams = `&salesOpportunitiesIds[]=`.concat(
        idsToGetConversion.join(`&salesOpportunitiesIds[]=`),
      );

      return getSalesOpportunityConversion({ urlParams: conversionParams });
    },
    enabled,
    groupBy: 'salesOpportunityId',
    idsToFetchConversion,
    queryKey: getSalesOpportunityConversionQueryKey(customerId),
  });
